import React, { forwardRef, useState, useRef, useEffect } from 'react';
import { Check, ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '../lib/cn';
import { cva, type VariantProps } from 'class-variance-authority';

const slideToConfirmVariants = cva(
  "relative flex h-14 items-center overflow-hidden rounded-full p-1",
  {
    variants: {
      variant: {
        primary: "bg-primary/10",
        destructive: "bg-destructive/10",
        success: "bg-success/10",
      },
    },
    defaultVariants: {
      variant: "primary",
    },
  }
);

const thumbVariants = cva(
  "absolute left-1 z-10 flex h-12 w-12 items-center justify-center rounded-full shadow-md transition-colors",
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground",
        destructive: "bg-destructive text-destructive-foreground",
        success: "bg-success text-white",
      },
      loading: {
        true: "opacity-80 pointer-events-none",
      },
      confirmed: {
        true: "pointer-events-none",
      }
    },
    defaultVariants: {
      variant: "primary",
    },
  }
);

export interface SlideToConfirmProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onPointerDown' | 'onPointerMove' | 'onPointerUp'>, VariantProps<typeof slideToConfirmVariants> {
  label: string;
  onConfirm: () => void;
  disabled?: boolean;
  loading?: boolean;
}

export const SlideToConfirm = forwardRef<HTMLDivElement, SlideToConfirmProps>(
  ({ className, variant, label, onConfirm, disabled, loading, ...props }, ref) => {
    const [progress, setProgress] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const THRESHOLD = 0.8; // 80% to confirm

    useEffect(() => {
      if (confirmed && !loading && !disabled) {
        // Reset if needed? No, stays confirmed until unmounted or reset via props.
        // Wait, if loading becomes false and confirmed is true, maybe reset if we need to?
        // Usually, the parent removes the component or we leave it checked.
      }
    }, [confirmed, loading, disabled]);

    // Reset if it gets disabled after being confirmed (maybe the action failed)
    useEffect(() => {
      if (disabled && !loading) {
        setProgress(0);
        setConfirmed(false);
      }
    }, [disabled, loading]);

    const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
      if (disabled || loading || confirmed) return;
      setIsDragging(true);
      if (containerRef.current) {
        containerRef.current.setPointerCapture(e.pointerId);
      }
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDragging || disabled || loading || confirmed || !containerRef.current) return;
      
      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const thumbWidth = 48; // h-12 w-12 -> 48px
      
      const maxDrag = rect.width - thumbWidth - 8; // 8px padding (left 4 + right 4)
      let currentX = e.clientX - rect.left - thumbWidth / 2;
      
      if (currentX < 0) currentX = 0;
      if (currentX > maxDrag) currentX = maxDrag;
      
      const newProgress = currentX / maxDrag;
      setProgress(newProgress);
    };

    const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDragging || disabled || loading || confirmed) return;
      setIsDragging(false);
      
      if (containerRef.current) {
        containerRef.current.releasePointerCapture(e.pointerId);
      }

      if (progress >= THRESHOLD) {
        setProgress(1);
        setConfirmed(true);
        onConfirm();
      } else {
        setProgress(0);
      }
    };

    const textOpacity = Math.max(0, 1 - progress * 2);

    return (
      <div
        ref={(node) => {
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
          // @ts-ignore
          containerRef.current = node;
        }}
        className={cn(slideToConfirmVariants({ variant }), className, disabled && "opacity-50 pointer-events-none")}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        role="button"
        aria-disabled={disabled || loading || confirmed}
        tabIndex={disabled || loading || confirmed ? -1 : 0}
        {...props}
      >
        <div
          className={cn(
            thumbVariants({ variant, loading: !!loading, confirmed }),
            !isDragging && "transition-transform duration-300"
          )}
          style={{
            transform: `translateX(calc(${progress} * (100cqw - 56px)))`,
          }}
        >
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : confirmed ? (
            <Check className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
        </div>
        
        <div
          className="absolute inset-0 flex items-center justify-center text-sm font-medium transition-opacity duration-200 pointer-events-none"
          style={{ opacity: textOpacity }}
        >
          <span className={cn(
            variant === 'primary' && "text-primary",
            variant === 'destructive' && "text-destructive",
            variant === 'success' && "text-success",
          )}>
            {label}
          </span>
        </div>
        
        {/* Fill background */}
        <div 
          className={cn(
            "absolute left-0 top-0 bottom-0 z-0 rounded-full transition-all opacity-50",
            variant === 'primary' && "bg-primary/20",
            variant === 'destructive' && "bg-destructive/20",
            variant === 'success' && "bg-success/20",
            !isDragging && "duration-300"
          )}
          style={{ width: `calc(${progress * 100}% + 48px)` }}
        />
      </div>
    );
  }
);

SlideToConfirm.displayName = 'SlideToConfirm';
